import { InPlaceEditor } from '@syncfusion/ej2-inplace-editor';

import { routes } from '@/shared/routes';
import { SubTask } from '@/shared/types/subTask';
import { formatDate } from '@/shared/utils/format';

import { POST } from '@/front/fetch';

let dragSource: HTMLTableRowElement | null;
let dragTarget: HTMLTableRowElement | null;

let dragTimeout: NodeJS.Timeout | undefined;

const update = async (table: HTMLTableElement) => {
  const ajax = table.dataset.ajax === 'true';

  const form = table.closest('form');

  if (form) {
    if (ajax) {
      const taskId = table.dataset.taskId;
      const recurring = table.dataset.recurring === 'true';

      const url = recurring ? routes.recurringTasks.subTasks({ id: taskId }) : routes.tasks.subTasks({ id: taskId });

      const data: SubTask[] = [...table.querySelectorAll('tr.sub-task')].map((tr, order) => {
        const titleDiv = tr.querySelector('div[data-name="title"]') as HTMLDivElement | null;
        const completedCheckBox = tr.querySelector('input[type="checkbox"]') as HTMLInputElement | null;

        return {
          task: titleDiv?.dataset.value ?? null,
          order,
          ...(completedCheckBox
            ? {
                completed: completedCheckBox.checked,
                completedDate: completedCheckBox.dataset.completedDate,
              }
            : {}),
        };
      });

      await POST(url, data);
    } else {
      form.dataset.dirty = 'true';
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  [...document.querySelectorAll('table.sub-tasks tr')]
    .map((tr) => tr as HTMLTableRowElement)
    .forEach((tr) => {
      if (tr.classList.contains('sub-task')) {
        initSubTaskRow(tr as HTMLTableRowElement);
      } else if (tr.classList.contains('sub-task-new')) {
        const table = tr.closest('table') as HTMLTableElement;

        const recurring = table.dataset.recurring === 'true';
        const ajax = table.dataset.ajax === 'true';

        const titleTextBox = tr.querySelector('input[type="text"]') as HTMLInputElement;
        const addButton = tr.querySelector('a.add') as HTMLAnchorElement;

        const addSubTask = async () => {
          const title = titleTextBox?.value.trim();

          if (title) {
            titleTextBox.value = '';

            let newTr = document.createElement('tr');

            tr.parentNode!.insertBefore(newTr, tr);

            newTr.outerHTML = subTaskRowTemplate({ title, recurring, ajax });

            newTr = tr.previousElementSibling! as HTMLTableRowElement;

            initSubTaskRow(newTr);

            await update(table);
          }
        };

        titleTextBox.addEventListener('keyup', async (e) => {
          addButton.classList.toggle('active', !!titleTextBox.value.trim());

          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();

            await addSubTask();
          }
        });

        addButton.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          await addSubTask();
        });
      }
    });
});

const initSubTaskRow = (tr: HTMLTableRowElement) => {
  const table = tr.closest('table') as HTMLTableElement;

  const titleInput = tr.querySelector('input[name="subTaskTitles[]"') as HTMLInputElement | null;

  const titleDiv = tr.querySelector('div[data-name="title"]') as HTMLDivElement | null;
  const completedCheckBox = tr.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
  const completedDateTd = tr.querySelector('td.completed-date') as HTMLTableCellElement | null;
  const deleteButton = tr.querySelector('a.delete') as HTMLAnchorElement | null;

  if (titleInput) {
    titleInput.addEventListener('keyup', async () => {
      await update(table);
    });
  } else if (titleDiv) {
    titleDiv.setAttribute('data-underline', 'false');

    const titleEditor = new InPlaceEditor({
      mode: 'Inline',
      name: titleDiv.dataset.name,
      value: titleDiv.dataset.value,
      actionOnBlur: 'Cancel',

      model: {
        autocomplete: 'off',
        showClearButton: false,
      },
      validationRules: {
        title: { required: true },
      },
      submitClick: async () => {
        titleDiv.dataset.value = titleEditor.value.toString();
        await update(table);
      },
    });

    titleEditor.appendTo(titleDiv);
  }

  if (completedCheckBox) {
    completedCheckBox.addEventListener('change', async () => {
      const completed = completedCheckBox.checked;

      if (completed) {
        if (completedDateTd) {
          completedDateTd.innerHTML = formatDate(new Date());
        }
      } else {
        delete completedCheckBox.dataset.completedDate;

        if (completedDateTd) {
          completedDateTd.innerHTML = '';
        }
      }

      tr.classList.toggle('completed', completed);

      await update(table);
    });
  }

  if (deleteButton) {
    deleteButton.addEventListener('click', async (e) => {
      e.preventDefault();

      tr.remove();

      await update(table);
    });
  }

  tr.addEventListener('dragstart', async (e) => {
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
      e.dataTransfer.effectAllowed = 'move';
    }

    dragSource = e.currentTarget as HTMLTableRowElement;
  });

  tr.addEventListener('dragover', async (e) => {
    e.preventDefault();

    if (dragSource && !dragTimeout) {
      const target = e.currentTarget as HTMLTableRowElement;

      if (target !== dragSource) {
        dragTarget = target;

        dragSource.style.opacity = '0';

        dragTimeout = setTimeout(() => {
          if (dragSource && dragTarget) {
            const rows = [...dragTarget.parentElement!.children];

            if (rows.indexOf(dragTarget) > rows.indexOf(dragSource)) {
              dragTarget.after(dragSource);
            } else {
              dragTarget.before(dragSource);
            }
          }

          dragTimeout = undefined;
        }, 100);
      }
    }
  });

  tr.addEventListener('drop', async (e) => {
    e.preventDefault();

    if (dragSource && dragTarget) {
      dragSource.style.opacity = '1';

      await update(table);
    }
  });
};

const subTaskRowTemplate = (params: { title: string; recurring?: boolean; ajax?: boolean }) => {
  const { title, recurring, ajax } = params;

  return ajax
    ? `
      <tr class="sub-task" draggable="true">
        <td>
          <span class="reorder">
            <i class="fa-solid fa-grip-dots-vertical"></i>
          </span>
        </td>
        ${
          !recurring
            ? `
        <th>
          <input type="checkbox" class="form-check-input" />
        </th>`
            : ''
        }
        <td class="w-100">
          <div data-name="title" data-value="${title.replaceAll('"', '&quot;')}" data-fullwidth="true"></div>
        </td>
        ${
          !recurring
            ? `
        <td class="completed-date"></td>`
            : ''
        }
        <td>
          <a href="#" class="delete">
            <i class="fa-solid fa-fa-trash"></i>
          </a>
        </td>
      </tr>`
    : `
      <tr class="sub-task" draggable="true">
        <td>
          <span class="reorder">
            <i class="fa-solid fa-grip-dots-vertical"></i>
          </span>
        </td>
        ${
          !recurring
            ? `
        <th>
          <input name="subTaskCompleteds[]" type="checkbox" class="form-check-input" />
        </th>`
            : ''
        }
        <td class="w-100">
          <input name="subTaskTitles[]" type="text" value="${title.replaceAll('"', '&quot;')}"></div>
        </td>
        ${
          !recurring
            ? `
        <td class="completed-date"></td>`
            : ''
        }
        <td>
          <a href="#" class="delete">
            <i class="fa-solid fa-fa-trash"></i>
          </a>
        </td>
      </tr>`;
};
